import type { useCatalogFiltersStore } from 'features/catalog/store'
import type { FilterControlProps } from 'features/charts-v2'
import type { ChartPanel } from 'features/charts-v2/types/filter-hash/v2'
import { savedFilterToPanelUpdates } from 'features/charts-v2/utils'
import {
  type FilterAppliedProps,
  type LogicalSearchOperator,
  trackFilterApplied,
  trackSavedFilterLoaded,
  trackSearchPerformed,
} from 'trackers/shared-events'
import type { FilterPreset, Surface } from 'types/project'

// ---------------------------------------------------------------------------
// Module-scope handler factories for use-catalog-filter-sidebar — pulled out
// so the hook itself stays under the line/nested-function thresholds. Mirrors
// `features/feedback/hooks/feedback-filter-sidebar-handlers.ts` exactly, just
// typed against `CatalogFiltersActions`; the panel switcher / panelsCount
// concept from that sibling is dropped since catalog has exactly one panel.
// ---------------------------------------------------------------------------

type CatalogFiltersActions = ReturnType<typeof useCatalogFiltersStore.getState>

export function getPanelFilterSelection(
  panel: ChartPanel,
  field: string,
): ReturnType<FilterControlProps['getFilterSelection']> {
  return panel.filters.find((f) => f.field === field) || null
}

export function getPanelGhostSelection(
  ghostFilters: readonly ChartPanel['filters'][number][],
  field: string,
): ReturnType<FilterControlProps['getFilterSelection']> {
  return ghostFilters.find((f) => f.field === field) || null
}

function deriveFilterFieldType(
  field: string,
  hasRange: boolean,
): FilterAppliedProps['filter_field_type'] {
  if (hasRange) return 'numeric'
  if (field.startsWith('custom')) return 'custom_field'
  if (field === 'sentiment') return 'sentiment'
  if (field === 'rating') return 'rating'
  if (field === 'create_time') return 'date'
  return 'text'
}

export function buildUpdateFilterSelectionHandler(
  panel: ChartPanel,
  updateFilterSelection: CatalogFiltersActions['updateFilterSelection'],
  surface: Surface,
): FilterControlProps['updateFilterSelection'] {
  return (field, operator, values, range) => {
    try {
      const prev = panel.filters.find((f) => f.field === field)
      const hasRange =
        range?.numericValue !== undefined ||
        range?.min !== undefined ||
        range?.max !== undefined ||
        range?.valueType !== undefined
      const isDelete = values.length === 0 && !hasRange
      let action: FilterAppliedProps['action']
      if (isDelete) action = 'remove'
      else if (!prev) action = 'add'
      else action = 'edit'
      trackFilterApplied({
        surface,
        location: 'sidebar',
        filter_field: field,
        filter_field_type: deriveFilterFieldType(field, hasRange),
        value_count: values.length,
        action,
      })
    } catch (e) {
      // eslint-disable-next-line no-console -- intentional error logging for tracker diagnostics
      console.error('Filter Applied tracking failed', e)
    }
    updateFilterSelection(panel.id, field, operator, values, range)
  }
}

export function buildUpdateSmartSearchHandler(
  panel: ChartPanel,
  updatePanel: CatalogFiltersActions['updatePanel'],
  surface: Surface,
): FilterControlProps['updateSmartSearch'] {
  return (value) => {
    if (value === null) {
      trackSearchPerformed({
        surface,
        location: 'sidebar',
        search_type: 'semantic',
        query_text: '',
        query_length: 0,
        n_terms: 0,
        action: 'remove',
        has_filters: panel.filters.length > 0,
        result_count: null,
      })
      updatePanel(panel.id, { searchTerms: [], smartSearchEnabled: false })
    } else if (value === undefined) {
      updatePanel(panel.id, { searchTerms: [], smartSearchEnabled: true })
    } else {
      trackSearchPerformed({
        surface,
        location: 'sidebar',
        search_type: 'semantic',
        query_text: value,
        query_length: value.length,
        n_terms: 1,
        action: 'submit',
        has_filters: panel.filters.length > 0,
        result_count: null,
      })
      updatePanel(panel.id, { searchTerms: [value], smartSearchEnabled: true })
    }
  }
}

export function buildUpdateKeywordFiltersHandler(
  panel: ChartPanel,
  updatePanel: CatalogFiltersActions['updatePanel'],
  surface: Surface,
): FilterControlProps['updateKeywordFilters'] {
  return (filters) => {
    const cleaned: Record<string, string[]> = {}
    for (const [op, vals] of Object.entries(filters)) {
      if (vals !== null) cleaned[op] = vals
    }
    try {
      const prev = panel.searchCriteria
      const allOps = new Set([...Object.keys(prev), ...Object.keys(cleaned)])
      const cardCount = Object.keys(cleaned).length
      for (const op of allOps) {
        const before = prev[op] ?? []
        const after = cleaned[op] ?? []
        if (
          before.length === after.length &&
          before.every((v, i) => v === after[i])
        ) {
          continue
        }
        let action: 'add' | 'edit' | 'remove'
        if (before.length === 0) action = 'add'
        else if (after.length === 0) action = 'remove'
        else action = 'edit'
        trackSearchPerformed({
          surface,
          location: 'sidebar',
          search_type: 'logical',
          operator: op as LogicalSearchOperator,
          query_text: after.join(', '),
          query_length: after.join(', ').length,
          n_terms: after.length,
          search_card_count: cardCount,
          action,
          has_filters: panel.filters.length > 0,
          result_count: null,
        })
      }
    } catch (e) {
      // eslint-disable-next-line no-console -- intentional error logging for tracker diagnostics
      console.error('Search Performed tracking failed', e)
    }
    updatePanel(panel.id, { searchCriteria: cleaned })
  }
}

export function buildSavedFilterApplyHandler(
  panel: ChartPanel,
  updatePanel: CatalogFiltersActions['updatePanel'],
  surface: Surface,
): (preset: FilterPreset) => void {
  return (preset) => {
    trackSavedFilterLoaded({
      surface,
      filter_name: preset.name,
      criteria_count: preset.filters_v2.filters.length,
    })
    updatePanel(panel.id, savedFilterToPanelUpdates(preset))
  }
}

/** Assembles the complete `FilterControlProps` bag for catalog's single
 *  panel. Kept here (not in the hook) so `use-catalog-filter-sidebar.ts`
 *  stays under the hook line-count threshold — it just gathers store state
 *  and calls this. */
export function buildFilterControlProps(params: {
  panel: ChartPanel
  ghostFilters: readonly ChartPanel['filters'][number][]
  filterOptions: FilterControlProps['filterOptions']
  isFilterOptionsLoading: boolean
  surface: Surface
  setFieldForCounts: (field: string | null) => void
  actions: Pick<
    CatalogFiltersActions,
    | 'updateFilterSelection'
    | 'updatePanel'
    | 'enableFilter'
    | 'disableFilter'
    | 'dropGhost'
  >
}): FilterControlProps {
  const {
    panel,
    ghostFilters,
    filterOptions,
    isFilterOptionsLoading,
    surface,
    setFieldForCounts,
    actions,
  } = params

  return {
    filterOptions,
    isFilterOptionsLoading,
    onSelectedFieldChange: setFieldForCounts,
    selectedFilterOptions: panel.filters,
    showBreakdownHierarchy: panel.showBreakdownHierarchy ?? false,
    updateFilterSelection: buildUpdateFilterSelectionHandler(
      panel,
      actions.updateFilterSelection,
      surface,
    ),
    getFilterSelection: (field) => getPanelFilterSelection(panel, field),
    getGhostSelection: (field) => getPanelGhostSelection(ghostFilters, field),
    onToggleFilterDisabled: (field) => {
      if (getPanelGhostSelection(ghostFilters, field)) {
        actions.enableFilter(panel.id, field)
      } else {
        actions.disableFilter(panel.id, field)
      }
    },
    onRemoveGhost: (field) => actions.dropGhost(panel.id, field),
    smartSearchValue:
      panel.smartSearchEnabled === false
        ? null
        : panel.searchTerms[0] || undefined,
    keywordFilters: panel.searchCriteria,
    updateSmartSearch: buildUpdateSmartSearchHandler(
      panel,
      actions.updatePanel,
      surface,
    ),
    updateKeywordFilters: buildUpdateKeywordFiltersHandler(
      panel,
      actions.updatePanel,
      surface,
    ),
    onSavedFilterApply: buildSavedFilterApplyHandler(
      panel,
      actions.updatePanel,
      surface,
    ),
  }
}
